import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquare } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useAuth, authFetch } from "@/lib/auth";
import { useChat } from "@/lib/chat";
import { api, INR } from "@/lib/marketplace";

export default function Messages() {
  const { user, loading, login } = useAuth();
  const chat = useChat();
  const [convos, setConvos] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => { document.title = "Messages — Biker Boulevard"; }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    let dead = false;
    const load = async () => {
      try {
        const r = await authFetch("/api/conversations");
        if (r.ok && !dead) setConvos(await r.json());
      } catch {} finally { if (!dead) setBusy(false); }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { dead = true; clearInterval(t); };
  }, [user, loading]);

  if (loading || busy) {
    return <Shell><div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div></Shell>;
  }
  if (!user) {
    return (
      <Shell>
        <div className="pt-32 max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold">Sign in to see your messages</h2>
          <button onClick={login} className="mt-6 px-6 py-3 rounded-full bg-[#00cccc] text-black font-semibold">Continue with Google</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="pt-24 pb-20 max-w-[800px] mx-auto px-5 md:px-10" data-testid="messages-page">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Messages</h1>
        <div className="mt-6 space-y-2">
          {convos.length === 0 && <p className="text-sm text-zinc-400">No conversations yet. Chat with sellers directly from any listing.</p>}
          {convos.map((c) => {
            const cover = c.listing?.photo_ids?.[c.listing?.primary_photo_index] || c.listing?.photo_ids?.[0];
            return (
              <button key={c.conversation_id} onClick={() => chat?.openChat(c.listing_id, c.other_name, c.conversation_id)}
                className="w-full p-3 rounded-xl border border-white/10 bg-white/[0.03] hover:border-[#00cccc]/30 flex gap-3 text-left"
                data-testid={`convo-${c.conversation_id}`}>
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#1A1E23] shrink-0">
                  {cover ? <img src={api.photoUrl(cover)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{c.listing?.brand} {c.listing?.model}</div>
                  <div className="text-xs text-zinc-400">{c.role === "buyer" ? "Seller" : "Buyer"}: {c.other_name}</div>
                  <div className="text-xs text-zinc-500">{INR(c.listing?.expected_price)}</div>
                </div>
                <MessageSquare className="w-4 h-4 text-[#00cccc] shrink-0 mt-1" />
                {c.unread > 0 && (
                  <span data-testid={`convo-unread-${c.conversation_id}`} className="self-start px-1.5 py-px rounded-full bg-red-500 text-white text-[10px] font-bold shrink-0 mt-1.5 min-w-[16px] text-center leading-4">{c.unread}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10"><div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} /></div>
      {children}
      <Footer />
    </main>
  );
}
